import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Fab,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  NetworkCheck as TestIcon,
  Router as RouterIcon,
  SignalCellularAlt as SignalIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import OLTService, { OLT } from '../services/oltService';
import spopService from '../services/spopService';
import PONDataView from '../components/PONDataView';

interface SPOP {
  id: string;
  name: string;
  areaCode: string;
}

const OLTPage: React.FC = () => {
  const [olts, setOlts] = useState<OLT[]>([]);
  const [spops, setSpops] = useState<SPOP[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOlt, setSelectedOlt] = useState<OLT | null>(null);
  const [viewPonDialog, setViewPonDialog] = useState(false);
  const [addEditDialog, setAddEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editingOlt, setEditingOlt] = useState<OLT | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);

  // Form data for add/edit
  const [formData, setFormData] = useState({
    spop_id: '',
    type: 'HIOSO' as 'HIOSO' | 'HSGQ',
    ip_address: '',
    port: 80,
    username: '',
    password: '',
    status: 'aktif' as 'aktif' | 'nonaktif'
  });

  useEffect(() => {
    fetchOLTs();
    fetchSPOPs();
  }, []);

  const fetchOLTs = async () => {
    try {
      setLoading(true);
      const response = await OLTService.getAllOLTs();
      if (response.success) {
        setOlts(response.data);
      }
    } catch (error: any) {
      setError('Failed to fetch OLTs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSPOPs = async () => {
    try {
      const response = await spopService.getAllSPOPs();
      console.log('SPOP Response:', response.data); // Debug log
      
      if (response.data.success) {
        // Data SPOP ada di response.data.data.spops, bukan response.data.data
        const spopsData = response.data.data.spops || response.data.data;
        
        if (Array.isArray(spopsData)) {
          setSpops(spopsData);
          console.log('SPOPs set:', spopsData); // Debug log
        } else {
          console.error('SPOP data is not an array:', spopsData);
          setSpops([]); // Fallback to empty array
        }
      } else {
        console.error('SPOP response not successful:', response.data);
        setSpops([]); // Fallback to empty array
      }
    } catch (error) {
      console.error('Failed to fetch SPOPs:', error);
      setSpops([]); // Fallback to empty array
    }
  };

  const handleAddOlt = () => {
    setEditingOlt(null);
    setFormData({
      spop_id: '',
      type: 'HIOSO',
      ip_address: '',
      port: 80,
      username: '',
      password: '',
      status: 'aktif'
    });
    setAddEditDialog(true);
  };

  const handleEditOlt = (olt: OLT) => {
    setEditingOlt(olt);
    setFormData({
      spop_id: olt.spop_id,
      type: olt.type,
      ip_address: olt.ip_address,
      port: olt.port,
      username: olt.username,
      password: olt.password,
      status: olt.status
    });
    setAddEditDialog(true);
  };

  const handleSaveOlt = async () => {
    try {
      if (editingOlt) {
        await OLTService.updateOLT(editingOlt.id, formData);
        setSuccess('OLT updated successfully');
      } else {
        await OLTService.createOLT(formData);
        setSuccess('OLT created successfully');
      }
      setAddEditDialog(false);
      fetchOLTs();
    } catch (error: any) {
      setError('Failed to save OLT: ' + error.response?.data?.message || error.message);
    }
  };

  const handleDeleteOlt = async () => {
    if (!selectedOlt) return;
    
    try {
      await OLTService.deleteOLT(selectedOlt.id);
      setSuccess('OLT deleted successfully');
      setDeleteDialog(false);
      setSelectedOlt(null);
      fetchOLTs();
    } catch (error: any) {
      setError('Failed to delete OLT: ' + error.response?.data?.message || error.message);
    }
  };

  const handleTestConnection = async (olt: OLT) => {
    try {
      setTestingConnection(olt.id);
      const response = await OLTService.testConnection(olt.id);
      
      if (response.success) {
        setSuccess(`Connection test successful for ${olt.ip_address}`);
      } else {
        setError(`Connection test failed for ${olt.ip_address}: ${response.message}`);
      }
    } catch (error: any) {
      setError(`Connection test failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const handleViewPonData = (olt: OLT) => {
    setSelectedOlt(olt);
    setViewPonDialog(true);
  };

  const getStatusChip = (status: string) => {
    return (
      <Chip
        label={status}
        color={status === 'aktif' ? 'success' : 'default'}
        size="small"
      />
    );
  };

  const getTypeChip = (type: string) => {
    return (
      <Chip
        label={type}
        color={type === 'HIOSO' ? 'primary' : 'secondary'}
        size="small"
      />
    );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            OLT Management
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchOLTs}
              sx={{ mr: 2 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddOlt}
            >
              Add OLT
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SPOP</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Port</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : olts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No OLTs found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    olts.map((olt) => (
                      <TableRow key={olt.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {olt.spop?.name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {olt.spop?.areaCode || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getTypeChip(olt.type)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {olt.ip_address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {olt.port}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {olt.username}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(olt.status)}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Tooltip title="View PON Data">
                              <IconButton
                                size="small"
                                onClick={() => handleViewPonData(olt)}
                                color="primary"
                              >
                                <SignalIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Test Connection">
                              <IconButton
                                size="small"
                                onClick={() => handleTestConnection(olt)}
                                disabled={testingConnection === olt.id}
                                color="success"
                              >
                                {testingConnection === olt.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <TestIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditOlt(olt)}
                                color="warning"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedOlt(olt);
                                  setDeleteDialog(true);
                                }}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={addEditDialog} onClose={() => setAddEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOlt ? 'Edit OLT' : 'Add New OLT'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>SPOP</InputLabel>
                <Select
                  value={formData.spop_id}
                  onChange={(e) => setFormData({ ...formData, spop_id: e.target.value })}
                  label="SPOP"
                >
                  {Array.isArray(spops) && spops.map((spop) => (
                    <MenuItem key={spop.id} value={spop.id}>
                      {spop.name} ({spop.areaCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'HIOSO' | 'HSGQ' })}
                    label="Type"
                  >
                    <MenuItem value="HIOSO">HIOSO</MenuItem>
                    <MenuItem value="HSGQ">HSGQ</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'aktif' | 'nonaktif' })}
                    label="Status"
                  >
                    <MenuItem value="aktif">Aktif</MenuItem>
                    <MenuItem value="nonaktif">Non-aktif</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="IP Address"
                  value={formData.ip_address}
                  onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                  sx={{ flex: 2 }}
                />
                <TextField
                  fullWidth
                  label="Port"
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                  sx={{ flex: 1 }}
                />
              </Stack>
              
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </Stack>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveOlt} variant="contained">
            {editingOlt ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete OLT {selectedOlt?.ip_address}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteOlt} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* PON Data View Dialog */}
      <Dialog 
        open={viewPonDialog} 
        onClose={() => setViewPonDialog(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          PON Data - {selectedOlt?.spop?.name || 'Unknown SPOP'}
        </DialogTitle>
        <DialogContent>
          {selectedOlt && (
            <PONDataView 
              oltId={selectedOlt.id} 
              onError={(error) => setError(error)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPonDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OLTPage;
